/**
 * DialerTelephonyContext — Expose useDialerTelephonyNumbers comme un context.
 *
 * 2026-06-04 (Fowler etape 9 migration) — Le hook useDialerTelephonyNumbers
 * etait jusque-la consomme uniquement par DialerProviderInner. Pour que
 * les composants externes (SMSPanel, FloatingCallBar, SettingsScreen...)
 * puissent l'atteindre sans passer par useDialer() (la facade god-context),
 * on l'enveloppe ici dans un Provider classique.
 *
 * Responsabilite unique :
 *   Distribuer aux consommateurs externes le state telephony (twilioNumbers,
 *   activeCallerIds, activeCallerNumbers, selectedCallerId).
 *
 * Pattern : Provider qui CONSOMME le hook -> expose la meme shape en context.
 * Zero duplication d'etat : la source de verite reste le hook.
 */

import React, { createContext, useContext } from 'react';
// @ts-ignore — react-query en .js sans types
import { useQueryClient } from '@tanstack/react-query';
import { useDialerAuth } from './DialerAuthContext';
import {
  useDialerTelephonyNumbers,
  type DialerTelephonyState,
} from '@/hooks/useDialerTelephonyNumbers';

const DialerTelephonyContext = createContext<DialerTelephonyState | null>(null);

export function DialerTelephonyProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { callerData } = useDialerAuth();
  const queryClient = useQueryClient();
  const value = useDialerTelephonyNumbers({ callerData, queryClient });
  return (
    <DialerTelephonyContext.Provider value={value}>
      {children}
    </DialerTelephonyContext.Provider>
  );
}

export function useDialerTelephony(): DialerTelephonyState {
  const ctx = useContext(DialerTelephonyContext);
  if (!ctx) {
    throw new Error(
      'useDialerTelephony must be used within <DialerTelephonyProvider>',
    );
  }
  return ctx;
}
